import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../lib/axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calculator } from 'lucide-react';

// 1 hoja ≈ 500 palabras ≈ ~650 tokens (aprox. español, ~1.3 tokens/palabra).
const TOKENS_PER_HOJA = 650;

const nfInt = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

/**
 * Calculadora de decisión: "con N créditos (= Y MXN) en el modelo X, ≈ T tokens
 * ≈ H hojas". Convierte créditos -> tokens usando el mismo modelo de cobro
 * (costo USD del modelo × fx × margen ÷ valor_crédito), separado para entrada y
 * salida, y traduce a hojas para medir mentalmente.
 */
export default function CreditCalculator({ settings = {} }) {
  const [pricing, setPricing] = useState([]);
  const [modelId, setModelId] = useState('');
  const [credits, setCredits] = useState(100);

  // cfg y packages vienen del padre (AdminDashboard) -> se actualizan EN VIVO al
  // guardar la config (valor del crédito, margen, precios de paquetes). Antes se
  // fetcheaban una vez al montar y quedaban stale.
  const cfg = useMemo(() => ({
    credit_value_mxn: Number(settings.credit_value_mxn) || 0,
    fx_usd_mxn: Number(settings.fx_usd_mxn) || 0,
    credit_markup: Number(settings.credit_markup) || 0,
  }), [settings.credit_value_mxn, settings.fx_usd_mxn, settings.credit_markup]);
  const packages = settings.credit_packages || [];

  useEffect(() => {
    (async () => {
      try {
        const p = await axiosInstance.get('/admin/pricing');
        const rows = (p.data.pricing || []).filter((r) => r.active);
        setPricing(rows);
        if (rows[0]) setModelId(rows[0].model_id);
      } catch { /* interceptor maneja 403 */ }
    })();
  }, []);

  const model = useMemo(() => pricing.find((r) => r.model_id === modelId), [pricing, modelId]);

  const result = useMemo(() => {
    const n = Number(credits) || 0;
    const { credit_value_mxn: cv, fx_usd_mxn: fx, credit_markup: mk } = cfg;
    const mxn = n * cv;
    // tokens = créditos × valor_crédito × 1e6 / (usd_por_Mtok × fx × margen)
    const tokensFor = (usdPerMtok) => {
      const denom = (Number(usdPerMtok) || 0) * fx * mk;
      return denom > 0 ? (n * cv * 1e6) / denom : 0;
    };
    const tin = tokensFor(model?.input_cost_per_mtok);
    const tout = tokensFor(model?.output_cost_per_mtok);
    return {
      mxn,
      tin, tout,
      hin: tin / TOKENS_PER_HOJA,
      hout: tout / TOKENS_PER_HOJA,
    };
  }, [credits, cfg, model]);

  return (
    <div className="glass rounded-xl p-6 space-y-4" data-testid="credit-calculator">
      <span className="text-sm font-medium flex items-center gap-2">
        <Calculator className="w-4 h-4 text-purple-600" /> Calculadora de créditos → tokens → hojas
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Modelo</Label>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 text-sm"
            data-testid="calc-model">
            {pricing.length === 0 && <option value="">— sin modelos en el catálogo —</option>}
            {pricing.map((r) => (
              <option key={r.model_id} value={r.model_id}>{r.model_id}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Créditos</Label>
          <Input type="number" step="0.01" value={credits}
            onChange={(e) => setCredits(e.target.value)} data-testid="calc-credits" />
        </div>
      </div>

      {model ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div className="text-xs text-slate-500 dark:text-slate-400">Entrada (input)</div>
            <div className="text-lg font-semibold">{nfInt.format(result.tin)} tokens</div>
            <div className="text-sm text-slate-600 dark:text-slate-300">≈ {nf1.format(result.hin)} hojas</div>
            <div className="text-xs text-slate-400">${model.input_cost_per_mtok}/1M tok</div>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div className="text-xs text-slate-500 dark:text-slate-400">Salida (output)</div>
            <div className="text-lg font-semibold">{nfInt.format(result.tout)} tokens</div>
            <div className="text-sm text-slate-600 dark:text-slate-300">≈ {nf1.format(result.hout)} hojas</div>
            <div className="text-xs text-slate-400">${model.output_cost_per_mtok}/1M tok</div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">Selecciona un modelo del catálogo.</p>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">
        {Number(credits) || 0} créditos = ${(result.mxn || 0).toFixed(2)} MXN · 1 hoja ≈ {TOKENS_PER_HOJA} tokens
        (≈ 500 palabras). Estimación con el costo del catálogo × margen; el cobro real usa el costo reportado por OpenRouter.
      </p>

      {/* Ganancia por paquete: precio − costo real (créditos × valor ÷ margen). */}
      <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-2">
        <div className="text-sm font-medium">Ganancia por paquete</div>
        {(() => {
          const { credit_value_mxn: cv, credit_markup: mk } = cfg;
          const per100 = mk > 0 ? 100 * cv * (1 - 1 / mk) : 0;
          const pct100 = mk > 0 ? (1 - 1 / mk) * 100 : 0;
          return (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 pr-2">Paquete</th>
                      <th className="py-2 px-2 text-right">Créditos</th>
                      <th className="py-2 px-2 text-right">Precio</th>
                      <th className="py-2 px-2 text-right">Costo</th>
                      <th className="py-2 px-2 text-right">Ganancia</th>
                      <th className="py-2 pl-2 text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.length === 0 && (
                      <tr><td colSpan={6} className="py-2 text-slate-500 dark:text-slate-400">Sin paquetes configurados.</td></tr>
                    )}
                    {packages.map((p, i) => {
                      const price = Number(p.price_mxn) || 0;
                      const cr = Number(p.credits) || 0;
                      const cost = mk > 0 ? (cr * cv) / mk : 0;
                      const profit = price - cost;
                      const pct = price > 0 ? (profit / price) * 100 : 0;
                      return (
                        <tr key={p.id || i} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-2 pr-2">{p.name || p.id}</td>
                          <td className="py-2 px-2 text-right">{nfInt.format(cr)}</td>
                          <td className="py-2 px-2 text-right">${price.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right text-slate-500 dark:text-slate-400">${cost.toFixed(2)}</td>
                          <td className={`py-2 px-2 text-right font-medium ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>${profit.toFixed(2)}</td>
                          <td className="py-2 pl-2 text-right">{nf1.format(pct)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Por cada <strong>100 créditos</strong> (vendidos al valor del crédito): ganancia ≈ <strong>${per100.toFixed(2)} MXN</strong> ({nf1.format(pct100)}% del precio).
                El % de ganancia lo fija el margen: márgen 1.60 = 37.5%; para 60% de ganancia usa márgen 2.50.
                Costo = créditos × valor_crédito ÷ margen (asume consumo total).
              </p>
            </>
          );
        })()}
      </div>
    </div>
  );
}
